/*! moodicon-core v1.7 – shared between Shopify + server
 *  - Robust recolor (style + attributes + currentColor)
 *  - Inheritance only on shapes (never <svg>/<g>/…)
 *  - Default-black fallback only when mapped (#000→target)
 *  - Ring heuristic (only if #ffffff is mapped):
 *      In <g id="line">, fill small closed shapes with NO own fill using the mapped white target.
 *  - Fix für Height/width
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) define([], factory);
  else if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MoodiconCore = factory();
}(this, function () {
  "use strict";

  const NAMED = {
    black:'#000000', white:'#ffffff', red:'#ff0000', blue:'#0000ff',
    green:'#008000', yellow:'#ffff00', grey:'#808080', gray:'#808080'
  };
  const SHAPE_SEL = 'path,circle,ellipse,rect,polygon,polyline';
  const CONTAINER_TAGS = new Set(['svg','g','defs','clippath','mask','pattern','symbol']);
  // Heuristik: Ringe sind klein & rund
  const RING_MAX_AREA = 1000;        // px² – ggf. anpassen
  const RING_ROUND_TOL = 0.28;       // |w-h|/max(w,h) <= 0.28 → „rund genug“

  // ---------- helpers ----------
  function normalizeHex(c){
    if (c == null) return '';
    let v = String(c).trim(); if (!v) return '';
    v = v.toLowerCase();
    if (v === 'none' || v === 'transparent') return '';
    if (NAMED[v]) return NAMED[v];

    let m = v.match(/^#([0-9a-f]{3})$/i);
    if (m){ const s=m[1].toLowerCase(); return `#${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}`; }

    m = v.match(/^#([0-9a-f]{6})$/i);
    if (m) return `#${m[1].toLowerCase()}`;

    m = v.match(/^rgba?\(([^)]+)\)$/i);
    if (m){
      const p = m[1].split(',').map(x=>parseFloat(x.trim()));
      if (p.length>=3 && p.slice(0,3).every(Number.isFinite)){
        const [r,g,b] = p.slice(0,3).map(n=>Math.max(0,Math.min(255,Math.round(n))));
        return '#'+[r,g,b].map(n=>n.toString(16).padStart(2,'0')).join('');
      }
    }
    return '';
  }

	function sanitizeSVG(svg){
	  // Grundreinigung (nur Kopf/Kommentare, wie bisher)
	  var out = String(svg || '')
		.replace(/^\s*<\?xml[^>]*>\s*/i, '')
		.replace(/<!DOCTYPE[^>]*>/ig, '')
		.replace(/<!--[\s\S]*?-->/g, '');

	  // Öffnendes <svg …> isolieren
	  var m = out.match(/<svg\b[^>]*>/i);
	  if (m) {
		var tag = m[0];

		// width/height NUR am Root-Tag entfernen (und nur, wenn viewBox existiert)
		if (/\bviewBox\s*=\s*(['"][^'"]*['"]|\S+)/i.test(tag)) {
		  tag = tag
			.replace(/\swidth\s*=\s*(".*?"|'.*?'|\S+)/i, '')
			.replace(/\sheight\s*=\s*(".*?"|'.*?'|\S+)/i, '');
		}

		// xmlns am Root sicherstellen (wie bisher)
		if (!/\bxmlns\s*=/.test(tag)) {
		  tag = tag.replace(/^<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"');
		}

		// Root-Tag im String ersetzen
		out = out.slice(0, m.index) + tag + out.slice(m.index + m[0].length);
	  } else {
		// Fallback: falls das <svg> Muster unerwartet nicht matcht
		out = out.replace(/<svg\b(?![^>]*\bxmlns=)/i, '<svg xmlns="http://www.w3.org/2000/svg"');
	  }

	  return out;
	}

  // ---------- color extraction (overlay-aware; visible shapes only; black last) ----------
  function extractColors(svg){
    const s   = sanitizeSVG(svg || '');
    const doc = new DOMParser().parseFromString(String(s), 'image/svg+xml');
    const root = doc.documentElement;
    if (!root || root.tagName.toLowerCase() !== 'svg') return [];

    const RENDER_TAGS = new Set(['path','rect','circle','ellipse','polygon','polyline','line']);
    const SKIP_CONTAINER = new Set([
      'defs','symbol','clippath','mask','marker','pattern','lineargradient','radialgradient',
      'filter','metadata','foreignobject'
    ]);

    // Map: geometrySignature -> final fill color (last one wins)
    const finalFillByGeom = new Map();
    const strokeColors = new Set();

    function parseStyle(str){
      const o={}; String(str||'').split(';').forEach(d=>{
        const i=d.indexOf(':'); if(i>-1) o[d.slice(0,i).trim().toLowerCase()] = d.slice(i+1).trim();
      }); return o;
    }
    function isZeroOpacity(v){
      const t=String(v||'').trim().toLowerCase();
      if(!t) return false;
      const x=parseFloat(t); return !isNaN(x) ? x===0 : (t==='0' || t==='0.0');
    }
    function visible(el){
      const st = parseStyle(el.getAttribute('style')||'');
      const disp = (el.getAttribute('display')||st.display||'').trim().toLowerCase();
      const opac = (el.getAttribute('opacity')||st.opacity||'');
      return disp!=='none' && !isZeroOpacity(opac);
    }
    function normHexMaybe(v, baseColor){
      if(!v) return '';
      let t = String(v).trim();
      if (!t) return '';
      if (/^currentcolor$/i.test(t)) t = (baseColor||'').trim();
      const h = normalizeHex(t);
      return h || '';
    }
    function geomSignature(el){
      const tag = el.tagName.toLowerCase();
      // Geometrie-relevante Attribute je Typ
      if (tag === 'path')    return tag + '|' + (el.getAttribute('d')||'') + '|' + (el.getAttribute('transform')||'');
      if (tag === 'rect')    return tag + '|' + [el.getAttribute('x'),el.getAttribute('y'),el.getAttribute('width'),el.getAttribute('height'),el.getAttribute('rx')||'',el.getAttribute('ry')||'',el.getAttribute('transform')||''].join(',');
      if (tag === 'circle')  return tag + '|' + [el.getAttribute('cx'),el.getAttribute('cy'),el.getAttribute('r'),el.getAttribute('transform')||''].join(',');
      if (tag === 'ellipse') return tag + '|' + [el.getAttribute('cx'),el.getAttribute('cy'),el.getAttribute('rx'),el.getAttribute('ry'),el.getAttribute('transform')||''].join(',');
      if (tag === 'polygon' || tag === 'polyline')
                             return tag + '|' + (el.getAttribute('points')||'') + '|' + (el.getAttribute('transform')||'');
      if (tag === 'line')    return tag + '|' + [el.getAttribute('x1'),el.getAttribute('y1'),el.getAttribute('x2'),el.getAttribute('y2'),el.getAttribute('transform')||''].join(',');
      return tag + '|' + (el.getAttribute('transform')||'');
    }

    // Traversiere in DOM-Reihenfolge: spätere Elemente übermalen frühere
    (function walk(node){
      if (!node || node.nodeType !== 1) return;
      const tag = node.tagName.toLowerCase();

      if (SKIP_CONTAINER.has(tag)) return;
      if (!visible(node)) return;

      if (RENDER_TAGS.has(tag)){
        const st = parseStyle(node.getAttribute('style')||'');
        const baseColor = (node.getAttribute('color') || st.color || '').trim();

        // Füllfarbe (kann „none“ sein)
        let fill = node.getAttribute('fill');
        if(!fill && Object.prototype.hasOwnProperty.call(st,'fill')) fill = st.fill;
        const fillHex = normHexMaybe(fill, baseColor);

        // Strichfarbe
        let stroke = node.getAttribute('stroke');
        if(!stroke && Object.prototype.hasOwnProperty.call(st,'stroke')) stroke = st.stroke;
        const strokeHex = normHexMaybe(stroke, baseColor);

        // „last wins“ je Geometrie für Füllung
        if (fillHex && fillHex !== '#000000'){ // Schwarz als Füllung taucht selten auf; wird unten global ans Ende geschoben
          finalFillByGeom.set(geomSignature(node), fillHex);
        } else if (fillHex === '#000000') {
          // Auch Schwarz merken; Reihenfolge später normalisieren
          finalFillByGeom.set(geomSignature(node), fillHex);
        }

        if (strokeHex) strokeColors.add(strokeHex);
      }

      for (let c=node.firstElementChild; c; c=c.nextElementSibling) walk(c);
    })(root);

    // Endmenge aufbauen: finale Füllfarben (nach Overpaint) + Strichfarben
    const seen = new Set();
    const out  = [];

    for (const hex of finalFillByGeom.values()){
      if (hex && !seen.has(hex)){ seen.add(hex); out.push(hex); }
    }
    for (const hex of strokeColors){
      if (hex && !seen.has(hex)){ seen.add(hex); out.push(hex); }
    }

    // Schwarz ans Ende
    const ib = out.indexOf('#000000');
    if (ib > -1){ out.splice(ib,1); out.push('#000000'); }

    return out;
  }



  // ---------- recolor ----------
  const PROPS = ['fill','stroke','stop-color','color'];

  function parseStyle(str){
    const o={}; String(str||'').split(';').forEach(d=>{
      const i=d.indexOf(':'); if(i>-1) o[d.slice(0,i).trim().toLowerCase()] = d.slice(i+1).trim();
    }); return o;
  }
  function buildStyle(o){
    return Object.entries(o).filter(([,v])=>v!=='' && v!=null).map(([k,v])=>`${k}:${v}`).join(';');
  }
  function hasOwnStyleProp(el, prop){
    if (!el.hasAttribute('style')) return false;
    const st = parseStyle(el.getAttribute('style'));
    return Object.prototype.hasOwnProperty.call(st, prop);
  }
  function getOwn(el, prop){
    if (hasOwnStyleProp(el, prop)) { const st=parseStyle(el.getAttribute('style')); return st[prop]||''; }
    if (el.hasAttribute(prop)) return (el.getAttribute(prop)||'').trim();
    return '';
  }
  function getInherited(el, prop){
    // own first
    const own = getOwn(el, prop);
    if (own) return own;
    // climb (stop at <svg>)
    let p = el.parentElement;
    while (p && p.tagName && p.tagName.toLowerCase()!=='svg'){
      const v = getOwn(p, prop);
      if (v) return v;
      p = p.parentElement;
    }
    // defaults
    if (prop==='fill' || prop==='color') return 'black';
    if (prop==='stroke' || prop==='stop-color') return '';
    return '';
  }
  function isContainer(el){
    const t = el.tagName && el.tagName.toLowerCase();
    return CONTAINER_TAGS.has(t||'');
  }
  function isShape(el){ return el.matches ? el.matches(SHAPE_SEL) : false; }
  function isClosedPath(el){
    if (!el || el.tagName.toLowerCase()!=='path') return false;
    const d = (el.getAttribute('d')||'').trim();
    return /z\b/i.test(d);
  }
  function approxRound(el){
    try{
      const bb = el.getBBox(); if (!bb || !bb.width || !bb.height) return false;
      const w = bb.width, h = bb.height;
      const area = w*h; if (area > RING_MAX_AREA) return false;
      const roundness = Math.abs(w-h)/Math.max(w,h);
      return roundness <= RING_ROUND_TOL;
    }catch(_){ return false; }
  }

  function recolorSVG(svg, rawMap){
    // normalize mapping
    const mapFromTo = {};
    Object.keys(rawMap||{}).forEach(k=>{
      const f=normalizeHex(k), t=normalizeHex(rawMap[k]);
      if (f && t) mapFromTo[f]=t;
    });
    if (!Object.keys(mapFromTo).length) return sanitizeSVG(svg);

    const doc = new DOMParser().parseFromString(String(svg||''),'image/svg+xml');
    const root = doc.documentElement; if (!root) return sanitizeSVG(svg);

    const hasMapBlack = Object.prototype.hasOwnProperty.call(mapFromTo,'#000000');
    const hasMapWhite = Object.prototype.hasOwnProperty.call(mapFromTo,'#ffffff');

    // PASS 1: direct style/attributes (+ currentColor)
    doc.querySelectorAll('*').forEach(el=>{
      if (isContainer(el)) return;

      if (el.hasAttribute('style')){
        const st = parseStyle(el.getAttribute('style')); let ch=false;
        PROPS.forEach(p=>{
          if (!(p in st)) return;
          let hv = normalizeHex(st[p]);
          if (hv && mapFromTo[hv]) { st[p]=mapFromTo[hv]; ch=true; }
          else if (String(st[p]).trim().toLowerCase()==='currentcolor' && st.color){
            const base = normalizeHex(st.color);
            if (base && mapFromTo[base]) { st.color=mapFromTo[base]; ch=true; }
          }
        });
        if (ch) el.setAttribute('style', buildStyle(st));
      }

      PROPS.forEach(a=>{
        if (!el.hasAttribute(a)) return;
        const hv = normalizeHex(el.getAttribute(a));
        if (hv && mapFromTo[hv]) el.setAttribute(a, mapFromTo[hv]);
        else if (String(el.getAttribute(a)).trim().toLowerCase()==='currentcolor' && el.hasAttribute('color')){
          const base = normalizeHex(el.getAttribute('color'));
          if (base && mapFromTo[base]) el.setAttribute('color', mapFromTo[base]);
        }
      });
    });

    // PASS 2: inheritance → only shapes
    doc.querySelectorAll(SHAPE_SEL).forEach(el=>{
      PROPS.forEach(p=>{
        const hasOwn = hasOwnStyleProp(el,p) || el.hasAttribute(p);
        if (hasOwn) return;
        let v = getInherited(el, p);
        if (!v) return;
        if (String(v).trim().toLowerCase()==='currentcolor') v = getInherited(el,'color');
        const hv = normalizeHex(v);
        if (hv && mapFromTo[hv]) {
          if (p==='color') el.setAttribute('color', mapFromTo[hv]);
          else el.setAttribute(p, mapFromTo[hv]);
        }
      });
    });

    // PASS 3: ring heuristic → only in g#line, only small closed/round shapes with NO own fill
    if (hasMapWhite){
      const line = doc.querySelector('g#line');
      if (line){
        line.querySelectorAll(SHAPE_SEL).forEach(el=>{
          // own fill?
          const hasOwnFill = hasOwnStyleProp(el,'fill') || el.hasAttribute('fill');
          if (hasOwnFill) return;

          // sonstige Filter: geschlossen ODER rund+klein
          const closed = isClosedPath(el);
          const looksRound = approxRound(el);

          if (!closed && !looksRound) return;

          // füllen mit Zielwert für #ffffff
          el.setAttribute('fill', mapFromTo['#ffffff']);
        });
      }
    }

    // PASS 4: default black → only when mapped, only shapes without own fill
    if (hasMapBlack){
      doc.querySelectorAll(SHAPE_SEL).forEach(el=>{
        const hasOwnFill  = hasOwnStyleProp(el,'fill') || el.hasAttribute('fill');
        if (hasOwnFill) return;
        const inh = normalizeHex(getInherited(el,'fill'));
        if (!inh || inh==='#000000'){
          el.setAttribute('fill', mapFromTo['#000000']);
        }
      });
    }

    return sanitizeSVG(new XMLSerializer().serializeToString(root));
  }

  // ---------- utils ----------
const PREVIEW_BASE = 'https://preview.moodicon.fashion/preview.php';

function buildPreviewQuery(emojiCode, slots) {
  const url = new URL(PREVIEW_BASE);
  const qs  = url.searchParams;

  const code = String(emojiCode || '').trim().toUpperCase();
  if (code) qs.set('emoji', code);

  Object.keys(slots || {})
    .sort((a, b) => {
      const ai = Number(String(a).replace(/^c/i, '')) || 0;
      const bi = Number(String(b).replace(/^c/i, '')) || 0;
      return ai - bi;
    })
    .forEach(k => {
      const idx = String(k).replace(/^c/i, '');
      const val = slots[k];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        qs.set('c' + idx, String(val));
      }
    });

  return url.toString(); // -> z.B. "https://preview.moodicon.fashion/preview.php?emoji=1F680&c1=001&c2=205..."
}


  return { normalizeHex, sanitizeSVG, extractColors, recolorSVG, buildPreviewQuery };
}));

